import React, { useId } from "react";
import { strings } from "../../resources/strings";

export const SEARCH_MAX_LENGTH = 100;

type ResourceSearchProps = {
  value: string;
  onChange: (value: string) => void;
  /** Clears the search; the field keeps focus. */
  onClear: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
};

/**
 * A visible label, `type="search"` for the search role and the mobile Search
 * key, and our own clear button: the browser's built-in one (Chrome, Safari)
 * is hidden so there are never two, and Firefox has none. Escape clears the
 * text explicitly, because native Escape handling differs by browser and can
 * fall out of step with a controlled input.
 */
const ResourceSearch: React.FC<ResourceSearchProps> = ({
  value,
  onChange,
  onClear,
  inputRef,
}) => {
  const inputId = useId();
  const clear = () => {
    onClear();
    inputRef.current?.focus();
  };
  return (
    <div role="search" className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-sm font-semibold text-gray-900">
        {strings.resources.searchLabel}
      </label>
      <div className="relative w-full max-w-md">
        <input
          ref={inputRef}
          id={inputId}
          type="search"
          value={value}
          maxLength={SEARCH_MAX_LENGTH}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape" && value) {
              event.preventDefault();
              clear();
            }
          }}
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          // 16px text stops iOS Safari zooming in on focus. The border is the
          // input's only boundary, so it keeps 3:1 against the page (1.4.11).
          className="input input-bordered w-full pr-12 text-base border-gray-500 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
        />
        {value && (
          <button
            type="button"
            onClick={clear}
            aria-label={strings.resources.clearSearchText}
            className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <svg
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className="h-5 w-5"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default ResourceSearch;
